import React, { useState } from 'react';
import UISectionWrapper, { UISectionHeader, UISectionBody } from '@/components/shared/UISectionWrapper';
import UIIconButton from '@/components/shared/UIIconButton';
import UIBadge from '@/components/shared/UIBadge';
import UIFieldInfo from '@/components/shared/UIFieldInfo';
import UIAvatarImage from '@/components/shared/UIAvatarImage';
import { FileResponseDto } from '@/api/files';
import './TenantOverviewSection.scss';

interface TenantOverviewData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  lease: string;
  moveIn: string;
  moveOut: string;
  rent: string;
  deposit: string;
  status: 'paid' | 'pending' | 'overdue';
  avatar?: string | null;
  personal_country?: string;
  personal_province?: string;
  personal_city?: string;
  personal_address?: string;
  personal_apt_suite?: string;
  personal_unit?: string;
  personal_municipality?: string;
  personal_postal_code?: string;
  documents: FileResponseDto[];
}

interface TenantOverviewSectionProps {
  data: TenantOverviewData;
  onContactTenant?: () => void;
  onEditTenant?: () => void;
}

const TenantOverviewSection: React.FC<TenantOverviewSectionProps> = ({
                                                                       data,
                                                                       onContactTenant = () => console.log('Contact tenant'),
                                                                       onEditTenant = () => console.log('Edit tenant')
                                                                     }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const CollapseButton = () => (
    <button className="TenantOverviewSection__collapse-btn" onClick={toggleExpanded}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d={isExpanded ? "M4 6L8 10L12 6" : "M6 4L10 8L6 12"}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const fullAddress = [
    data.personal_address,
    data.personal_apt_suite,
    data.personal_city,
    data.personal_province,
    data.personal_postal_code,
    data.personal_country,
  ].filter(Boolean).join(', ');

  return (
    <UISectionWrapper className="TenantOverviewSection">
      <UISectionHeader>
        <div className="TenantOverviewSection__header">
          <h3 className="TenantOverviewSection__title">Tenant overview</h3>
          <CollapseButton />
        </div>
        <div className="TenantOverviewSection__divider" />
      </UISectionHeader>

      {isExpanded && (
        <UISectionBody>
          <div className="TenantOverviewSection__content">
            {/* Left side - Photo, Badge, Buttons */}
            <div className="TenantOverviewSection__left">
              <div className="TenantOverviewSection__avatar">
                <UIAvatarImage 
                  avatar={data.avatar} 
                  alt={`${data.firstName} ${data.lastName}`} 
                  size="xxlarge" 
                />
              </div>

              <UIBadge variant="info" shape="default">
                {data.status}
              </UIBadge>

              <div className="TenantOverviewSection__buttons">
                <UIIconButton
                  iconType="contact"
                  variant="success"
                  size="medium"
                  onClick={onContactTenant}
                />
                <UIIconButton
                  iconType="edit"
                  variant="primary"
                  size="medium"
                  onClick={onEditTenant}
                />
              </div>
            </div>

            {/* Right side - Field Information */}
            <div className="TenantOverviewSection__info">
              <div className="TenantOverviewSection__field-group">
                <div className="TenantOverviewSection__field-labels">
                  <span className="TenantOverviewSection__field-label">Name:</span>
                  <span className="TenantOverviewSection__field-label">Phone:</span>
                  <span className="TenantOverviewSection__field-label">Email:</span>
                  <span className="TenantOverviewSection__field-label">Address:</span>
                  <span className="TenantOverviewSection__field-label">Municipality:</span>
                  <span className="TenantOverviewSection__field-label">Postal Code:</span>
                  <span className="TenantOverviewSection__field-label">Documents:</span>

                </div>

                <div className="TenantOverviewSection__field-values">
                  <UIFieldInfo text={`${data.firstName} ${data.lastName}`} />
                  <UIFieldInfo text={data.phone} isLink onClick={() => console.log('Call tenant')} />
                  <UIFieldInfo text={data.email} isLink onClick={() => console.log('Email tenant')} />
                  <UIFieldInfo
                    text={fullAddress}
                    iconType="location"
                  />
                  <UIFieldInfo text={data.personal_municipality || 'N/A'} />
                  <UIFieldInfo text={data.personal_postal_code || 'N/A'} />
                  <div className="TenantOverviewSection__document-list">
                    {data.documents && data.documents.length > 0 ? (
                      data.documents.map((doc) => (
                        <UIFieldInfo
                          key={doc.id}
                          text={doc.originalName}
                          iconType="doc"
                          isLink
                          onClick={() => window.open(doc.url, '_blank')}
                        />
                      ))
                    ) : (
                      <UIFieldInfo text="No documents uploaded" />
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};

export default TenantOverviewSection;
